import React, { useEffect, useState } from "react";
import { Redirect, Route } from "react-router-dom";
import { hasSubscriptionFeature, getFeatureUpgradeMessage } from "utils/utils";
import { authService } from "services";

/**
 * Componente de ruta que verifica si el usuario tiene acceso a una funcionalidad
 * según su tipo de suscripción, similar a PrivateRoute pero para suscripciones
 * 
 * @param {React.Component} component - El componente a renderizar si tiene acceso
 * @param {string} feature - La clave de la funcionalidad de SUBSCRIPTION_FEATURES
 * @param {string[]} roles - Roles permitidos (opcional, para combinar con suscripción)
 * @param {string} redirectTo - Ruta a la que redirigir si no tiene acceso (default: '/')
 * @param {function} onAccessDenied - Callback opcional cuando se deniega el acceso
 * 
 * @example
 * <SubscriptionRoute 
 *   exact 
 *   path="/forecasting" 
 *   feature="FORECASTING"
 *   roles={["ADMIN"]}
 *   component={Forecasting}
 *   redirectTo="/plans"
 * />
 */
export const SubscriptionRoute = ({
	component: Component,
	feature,
	roles,
	redirectTo = "/",
	onAccessDenied,
	...rest
}) => {
	const [isAuthenticated, setIsAuthenticated] = useState(null);

	useEffect(() => {
		const authenticate = async () => {
			if (!authService.currentUser) {
				const user = await authService.checkCookieAndSetToken();
				if (!user) {
					// Continuar solo si el usuario no ha sido autenticado por cookie
					setIsAuthenticated(false);
				} else {
					setIsAuthenticated(true);
				}
			} else {
				setIsAuthenticated(true);
			}
		};

		authenticate();
	}, []);

	// Mientras se autentica, muestra un loading o nada
	if (isAuthenticated === null) {
		return null;
	}

	return (
		<Route
			{...rest}
			render={(props) => {
				// Verificar autenticación primero
				if (!isAuthenticated) {
					return (
						<Redirect
							to={{ pathname: "/login", state: { from: props.location } }}
						/>
					);
				}

				const currentUser = authService.currentUser;

				// Verificar roles si se especificaron
				if (roles) {
					const matchRole = roles.some((r) => currentUser.roles.indexOf(r) >= 0);
					if (!matchRole) {
						return <Redirect to={{ pathname: "/" }} />;
					}
				}

				// Verificar acceso a la funcionalidad
				const hasAccess = hasSubscriptionFeature(feature);

				if (!hasAccess) {
					// Ejecutar callback si existe
					if (onAccessDenied) {
						const upgradeMessage = getFeatureUpgradeMessage(feature);
						onAccessDenied({ feature, upgradeMessage });
					}

					// Redirigir a la ruta especificada
					return (
						<Redirect
							to={{
								pathname: redirectTo,
								state: {
									from: props.location,
									subscriptionRequired: feature,
									message: getFeatureUpgradeMessage(feature),
								},
							}}
						/>
					);
				}

				// Usuario tiene acceso, renderizar componente
				return <Component {...props} />;
			}}
		/>
	);
};

export default SubscriptionRoute;

